// @ts-nocheck
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

/**
 * LiveTranscriptsProvider — store léger multi-appels des premiers segments de
 * transcription (style Minari : on montre les premières secondes du transcript
 * directement dans chaque ligne du tableau).
 *
 * Différent de LiveTranscript (qui ouvre un SSE riche pour UN appel dans la
 * modale) : ici on suit PLUSIEURS appels actifs en parallèle, mais on ne garde
 * qu'un aperçu court (≤ PREVIEW_WINDOW_MS d'audio, plafonné en caractères).
 *
 * Pour chaque callId enregistré via useTrackCall(), on ouvre un EventSource
 * dédié, on accumule les segments jusqu'à la fenêtre d'aperçu, puis on ferme le
 * flux (le transcript complet reste disponible dans la modale si l'agent
 * l'ouvre). Évite de garder N flux riches ouverts en permanence.
 */

const PREVIEW_WINDOW_MS = 10_000; // 10 premières secondes d'audio
const PREVIEW_MAX_CHARS = 140;

const LiveTranscriptsContext = createContext({
  previews: {},
  trackCall: (_callId) => {},
  untrackCall: (_callId) => {},
});

export function LiveTranscriptsProvider({ children }) {
  // previews : { [callId]: { text, done } }
  const [previews, setPreviews] = useState({});
  const sourcesRef = useRef(new Map()); // callId -> { es, accText }

  const closeSource = useCallback((callId) => {
    const entry = sourcesRef.current.get(callId);
    if (entry?.es) { try { entry.es.close(); } catch { /* noop */ } }
    sourcesRef.current.delete(callId);
  }, []);

  const trackCall = useCallback((callId) => {
    if (!callId || sourcesRef.current.has(callId)) return;
    sourcesRef.current.set(callId, { es: null, accText: '' });

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/sse/token', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'transcript', callId }),
        });
        if (!res.ok) { sourcesRef.current.delete(callId); return; }
        const { token } = await res.json();
        if (cancelled || !token) { sourcesRef.current.delete(callId); return; }

        const es = new EventSource(`/api/twilio/live-transcript/${callId}?token=${encodeURIComponent(token)}`);
        const entry = sourcesRef.current.get(callId);
        if (!entry) { try { es.close(); } catch { /* noop */ } return; }
        entry.es = es;

        es.onmessage = (event) => {
          let data;
          try { data = JSON.parse(event.data); } catch { return; }
          if (data.event === 'segment') {
            // On ne garde l'aperçu que des segments dans la fenêtre 10s.
            if (typeof data.timestamp === 'number' && data.timestamp > PREVIEW_WINDOW_MS) {
              setPreviews((prev) => ({ ...prev, [callId]: { text: prev[callId]?.text || '', done: true } }));
              closeSource(callId);
              return;
            }
            const cur = sourcesRef.current.get(callId);
            if (!cur) return;
            const speakerMark = data.speaker === 'inbound' ? '' : '› ';
            cur.accText = (cur.accText + ' ' + speakerMark + (data.text || '')).trim().slice(0, PREVIEW_MAX_CHARS);
            setPreviews((prev) => ({ ...prev, [callId]: { text: cur.accText, done: false } }));
          } else if (data.event === 'end' || data.event === 'no-session' || data.event === 'voicemail_detected') {
            setPreviews((prev) => ({ ...prev, [callId]: { text: prev[callId]?.text || '', done: true } }));
            closeSource(callId);
          }
        };
        es.onerror = () => { /* auto-reconnect géré par EventSource */ };
      } catch {
        sourcesRef.current.delete(callId);
      }
    })();
  }, [closeSource]);

  const untrackCall = useCallback((callId) => {
    closeSource(callId);
    setPreviews((prev) => {
      if (!(callId in prev)) return prev;
      const next = { ...prev };
      delete next[callId];
      return next;
    });
  }, [closeSource]);

  // Cleanup global au démontage
  useEffect(() => {
    const sources = sourcesRef.current;
    return () => {
      for (const [, entry] of sources) {
        if (entry?.es) { try { entry.es.close(); } catch { /* noop */ } }
      }
      sources.clear();
    };
  }, []);

  return (
    <LiveTranscriptsContext.Provider value={{ previews, trackCall, untrackCall }}>
      {children}
    </LiveTranscriptsContext.Provider>
  );
}

export function useLiveTranscripts() {
  return useContext(LiveTranscriptsContext);
}

/**
 * useTrackCall — s'abonne au transcript d'un appel actif tant que le composant
 * est monté et que callId est défini. Retourne l'aperçu courant ({text, done}).
 */
export function useTrackCall(callId) {
  const { previews, trackCall, untrackCall } = useLiveTranscripts();
  useEffect(() => {
    if (!callId) return;
    trackCall(callId);
    // On NE untrack PAS au démontage de la ligne (la ligne peut re-render) ;
    // le flux se ferme tout seul après la fenêtre d'aperçu ou la fin d'appel.
  }, [callId, trackCall]);
  return callId ? previews[callId] : undefined;
}
